import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { Button, Card } from "@cmsgov/ds-medicare-gov";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import uuid from "uuid";
import { Routes } from "../../app";
import { AppStore } from "../../app/store";
import { ActionType } from "../../@types";
import {
  displayPackage,
  displayDrug,
  sendDrugConfirmationTealiumEvent,
} from "../../helpers/prescriptionHelpers";
import { useTranslate, CONSTANTS, sendTealiumEvent } from "../../helpers";

interface ViewPrescriptionsProps {
  onEdit: (drug: {
    is_generic: boolean;
    rxcui: string;
    index: number;
    name: string;
  }) => void;
  addMoreDrugs: () => void;
  goToPharmacySelection: () => void;
  resetPrescriptionIndex: () => void;
}

const ViewPrescriptions: FunctionComponent<ViewPrescriptionsProps> = props => {
  const t = useTranslate();
  const {
    state: { beneficiary, prescriptions },
    dispatch,
  } = useContext(AppStore.AppContext);
  const [toastId, setToastId] = useState("");
  const TOAST_DURATION = 5000;

  useEffect(() => {
    setToastId(uuid());
  }, []);

  return (
    <div className="ViewPrescriptions">
      <h2 className="sr-only">{t("prescription.drug_list")}</h2>
      {beneficiary && (
        <p className="ds-u-measure--wide ds-u-margin-bottom--3 ds-u-margin-top--0">
          {t("view_prescriptions.drug_list_disclaimer")}
        </p>
      )}
      <ol className="viewPrescriptions__listCards ds-c-list--bare ds-u-padding--0">
        {prescriptions.map((prescription, index) => {
          return (
            <li key={index}>
              <Card className="ViewPrescriptions__prescriptionCard">
                <div className="ds-l-row">
                  <div className="ds-l-col--12 ds-l-sm-col--4">
                    <div>
                      <h3 className="ViewPrescriptions__prescriptionName e2e-drug-list-name">
                        {displayDrug(t, prescription)}
                      </h3>

                      {prescription.is_generic && (
                        <p className="ViewPrescriptions__prescriptionCardText">
                          {t("prescription.generic")}
                        </p>
                      )}
                    </div>
                  </div>

                  <div
                    className={`ds-l-col--12 ds-l-sm-col--3${
                      prescription.use_package_quantity
                        ? " ds-u-margin-top--2 ds-u-sm-margin-top--0"
                        : ""
                    }`}
                  >
                    {prescription.use_package_quantity && (
                      <>
                        {" "}
                        <h4 className="ViewPrescriptions__prescriptionCardLabel">
                          {t("prescription.package_type")}
                        </h4>
                        <p className="ViewPrescriptions__prescriptionCardText">
                          {displayPackage(t, prescription)}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="ds-l-col--12 ds-l-sm-col--2 ds-u-margin-top--2 ds-u-sm-margin-top--0">
                    <h4 className="ViewPrescriptions__prescriptionCardLabel">
                      {t("prescription.quantity")}
                    </h4>

                    <p className="ViewPrescriptions__prescriptionCardText">
                      {prescription.quantity}
                    </p>
                  </div>

                  <div className="ds-l-col--12 ds-l-sm-col--3 ds-u-margin-top--2 ds-u-sm-margin-top--0">
                    <h4 className="ViewPrescriptions__prescriptionCardLabel">
                      {t("prescription.frequency")}
                    </h4>

                    <p className="ViewPrescriptions__prescriptionCardText">
                      {t(
                        CONSTANTS.prescriptionFrequencyLabels[
                          prescription.frequency
                        ]
                      )}
                    </p>
                  </div>
                </div>

                <div className="ds-l-row ds-u-justify-content--between">
                  <div className="ds-l-col--6 ds-l-sm-col--5 ds-l-md-col--3">
                    <Button
                      variation="transparent"
                      className="ds-c-button--primary-alt ViewPrescriptions__linkButton"
                      aria-label={`${t("remove")} ${displayDrug(
                        t,
                        prescription
                      )}`}
                      onClick={(): void => {
                        dispatch({
                          type: ActionType.DELETE_PRESCRIPTION,
                          payload: { index },
                        });
                        props.resetPrescriptionIndex();

                        if (!toast.isActive(toastId)) {
                          toast(
                            <div className="ViewPrescriptions__toast">
                              <strong>{t("prescription.success")}</strong>
                              <p className="ds-u-margin--0">
                                {t("prescription.successfully_removed_drug")}
                              </p>
                            </div>,
                            {
                              toastId,
                              containerId: "globalToastContainer",
                              autoClose: TOAST_DURATION,
                              className: "ds-u-padding--0",
                              position: toast.POSITION.TOP_CENTER,
                              draggable: false,
                              hideProgressBar: true,
                              closeButton: false,
                            }
                          );
                        }
                      }}
                    >
                      {t("prescription.remove_drug")}
                    </Button>
                  </div>
                  <div className="ds-l-col--1 ds-l-sm-col--2 ds-l-md-col--6" />
                  <div className="ds-l-col--4 ds-l-sm-col--5 ds-l-md-col--3 ds-u-text-align--right ds-u-md-text-align--left">
                    <Button
                      variation="transparent"
                      className="ds-c-button--primary-alt ViewPrescriptions__linkButton"
                      aria-label={`${t("edit")} ${displayDrug(
                        t,
                        prescription
                      )}`}
                      onClick={(): void => {
                        // istanbul ignore next: tested elsewhere
                        sendTealiumEvent(
                          "Find Plans - Add Drugs",
                          "Edit Drug List"
                        );
                        props.onEdit({
                          is_generic: prescription.is_generic,
                          rxcui: prescription.rxcui,
                          index,
                          name: prescription.name,
                        });
                      }}
                    >
                      {t("prescription.edit_drug")}
                    </Button>
                  </div>
                </div>
              </Card>
            </li>
          );
        })}
      </ol>
      <div className="ds-u-margin-top--5 mct-u-full-width-mobile">
        {beneficiary && beneficiary.pdes && beneficiary.pdes.length > 0 && (
          <Link
            to={Routes.pdePage}
            className="ds-c-button ds-c-button--primary-alt-outline ds-u-margin-right--0 ds-u-sm-margin-right--2 ds-u-margin-bottom--2 ds-u-sm-margin-bottom--2 mct-u-full-width-mobile"
            onClick={(): void => {
              sendTealiumEvent(
                "Find Plans - Add Drugs",
                "Add Recently Filled Drugs"
              );
            }}
          >
            {t("view_prescriptions.add_recently_filled_drugs")}
          </Link>
        )}
        <Button
          onClick={(): void => {
            // istanbul ignore next: tested elsewhere
            sendTealiumEvent("Find Plans - Add Drugs", "Add Another Drug");
            props.addMoreDrugs();
          }}
          className="ds-c-button--primary-alt-outline e2e-drug-add-more mct-u-full-width-mobile"
        >
          {t("view_prescriptions.find_and_add_drug")}
        </Button>
      </div>
      <div className="ViewPrescriptions__footer">
        <Button
          variation="primary"
          onClick={(): void => {
            sendDrugConfirmationTealiumEvent(prescriptions);
            props.goToPharmacySelection();
          }}
          className="e2e-drug-next ds-u-margin-top--2 ds-u-sm-margin-top--0 mct-u-full-width-mobile"
          size="big"
        >
          {t("prescription.button.done_adding_drugs")}
        </Button>
      </div>
    </div>
  );
};

export default ViewPrescriptions;
